import React, { createContext, useContext, useEffect, useState } from 'react';
import { PORTCOS } from '../mock/platformData';
import { API_BASE, apiFetch } from '../api/base';
import { useAuth } from './AuthContext';
import type { Portco, PortcoDetail } from '../types';

export type PortCoFilter = 'all' | string;

interface PortCoContextValue {
  selectedPortco: PortCoFilter;
  setSelectedPortco: (id: PortCoFilter) => void;
  selectedPortcoName: string;
  selectedPortcoDetail: PortcoDetail | null;
  portcos: Portco[];
  /** True when the user may see the whole portfolio (portfolio admin). */
  canSeeAll: boolean;
}

const PortCoContext = createContext<PortCoContextValue>({
  selectedPortco: 'all',
  setSelectedPortco: () => {},
  selectedPortcoName: 'All Businesses',
  selectedPortcoDetail: null,
  portcos: PORTCOS,
  canSeeAll: true,
});

export const usePortCo = () => useContext(PortCoContext);

export const PortCoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const canSeeAll = !user || user.role === 'portfolio_admin' || user.tenants.includes('*');
  const [rawSelection, setRawSelection] = useState<PortCoFilter>('all');
  const [registryPortcos, setRegistryPortcos] = useState<Portco[]>([]);

  useEffect(() => {
    if (!user) return;
    let active = true;
    // apiFetch attaches the Entra Bearer token (cookie sessions still work)
    apiFetch(`${API_BASE}/integrations`).then((res) => {
      if (!res.ok) throw new Error(`Tenant registry: ${res.status}`);
      return res.json();
    }).then((data) => {
      if (active && Array.isArray(data.tenants)) {
        setRegistryPortcos(data.tenants.map((t: any) => ({
          id: t.id, name: t.name, code: t.code, region: t.region,
        })));
      }
    }).catch(() => { /* keep tenant-admin fallback below */ });
    return () => { active = false; };
  }, [user?.id]);

  // Tenant admins are locked to their assigned business — never 'all',
  // and the backend rejects 'all' for non-portfolio roles.
  const selectedPortco: PortCoFilter = canSeeAll ? rawSelection : (user?.tenants[0] ?? '');
  const setSelectedPortco = (id: PortCoFilter) => {
    if (canSeeAll) setRawSelection(id);
  };

  // Tenant admins with no registry copy yet still see their own business in the switcher
  const userPortcoFallback: Portco[] = (user && !canSeeAll)
    ? user.tenants.map((id) => {
        const known = PORTCOS.find((p) => p.id === id);
        return known ? { id: known.id, name: known.name, code: known.code, region: known.region }
                     : { id, name: id, code: '', region: '' };
      })
    : [];

  const visible = canSeeAll
    ? (registryPortcos.length ? registryPortcos : PORTCOS.map((p) => ({ id: p.id, name: p.name, code: p.code, region: p.region })))
    : registryPortcos.filter((p) => user?.tenants.includes(p.id));
  const portcos = visible.length ? visible : userPortcoFallback;

  const selectedPortcoDetail =
    selectedPortco === 'all'
      ? null
      : PORTCOS.find((p) => p.id === selectedPortco) ?? null;

  const selectedPortcoName =
    selectedPortco === 'all'
      ? 'All Businesses'
      : portcos.find((p) => p.id === selectedPortco)?.name ?? selectedPortco;

  return (
    <PortCoContext.Provider value={{ selectedPortco, setSelectedPortco, selectedPortcoName, selectedPortcoDetail, portcos, canSeeAll }}>
      {children}
    </PortCoContext.Provider>
  );
};
